import React, { useState } from 'react';
import { GraduationCap, BookOpen, Calendar, UserCheck, Phone, ArrowRight, Clock } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';

export const SchoolDemo: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState<'cursos' | 'aluno' | 'experiencia'>('cursos');

  const handleEnrollment = (courseName?: string) => {
    const msg = encodeURIComponent(`Olá, gostaria de solicitar informações e agendar uma aula experimental para o curso: ${courseName || 'Geral'}.`);
    window.open(`https://wa.me/554988416296?text=${msg}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#FAFAFC] text-slate-900 font-sans">
      {/* Top Banner */}
      <div className="bg-indigo-600 text-white px-4 py-2 text-center text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2">
        <GraduationCap className="w-4 h-4" />
        Matrículas Abertas para o Próximo Semestre — Vagas Limitadas
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-indigo-600 flex items-center justify-center text-white font-black text-xl shadow-md">
              E
            </div>
            <div>
              <span className="font-extrabold text-xl tracking-tight text-slate-900 block leading-none">Escola Modelo de Idiomas</span>
              <span className="text-[10px] text-indigo-600 font-bold uppercase tracking-widest">Ensino & Formação Internacional</span>
            </div>
          </div>

          <nav className="hidden md:flex gap-8 text-xs font-bold uppercase tracking-wider text-slate-600">
            <button onClick={() => setSelectedTab('cursos')} className={selectedTab === 'cursos' ? 'text-indigo-600 border-b-2 border-indigo-600 pb-1' : 'hover:text-slate-900'}>Cursos & Programas</button>
            <button onClick={() => setSelectedTab('aluno')} className={selectedTab === 'aluno' ? 'text-indigo-600 border-b-2 border-indigo-600 pb-1' : 'hover:text-slate-900'}>Área do Aluno (App)</button>
            <button onClick={() => setSelectedTab('experiencia')} className={selectedTab === 'experiencia' ? 'text-indigo-600 border-b-2 border-indigo-600 pb-1' : 'hover:text-slate-900'}>Aula Experimental</button>
          </nav>

          <button 
            onClick={() => handleEnrollment()}
            className="px-5 py-2.5 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-xs uppercase tracking-wider transition-colors shadow-md flex items-center gap-2"
          >
            <Phone className="w-4 h-4" /> Agendar Aula Experimental
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-6 bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto text-center">
          <Badge variant="indigo" className="mb-4">METODOLOGIA INTERATIVA 360º</Badge>
          <h1 className="text-4xl sm:text-6xl font-black text-slate-900 tracking-tight mb-6 leading-tight">
            Fale inglês com fluência e <br />
            <span className="text-indigo-600">confiança no mercado global.</span>
          </h1>
          <p className="text-lg text-slate-600 font-light max-w-2xl mx-auto leading-relaxed mb-10">
            Cursos presenciais e online com aplicativo exclusivo para prática diária, acompanhamento de faltas, notas e tarefas no celular.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button 
              onClick={() => handleEnrollment('Inglês Intensivo')}
              className="w-full sm:w-auto px-8 py-4 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm uppercase tracking-wider transition-all shadow-lg flex items-center justify-center gap-2"
            >
              Fazer Teste de Nível Grátis <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Interactive Tabs */}
      <section className="py-16 px-6 max-w-7xl mx-auto">
        {selectedTab === 'cursos' && (
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">Programas de Ensino em Destaque</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { title: 'Inglês para Negócios & Executive', level: 'Intermediário / Avançado', duration: '12 meses', desc: 'Focado em apresentações, reuniões internacionais e vocabulário corporativo.' },
                { title: 'Inglês Geral & Fluência Rápida', level: 'Iniciante ao Avançado', duration: '18 meses', desc: 'Desenvolva fala, escuta e leitura com professores nativos e aplicativo de suporte.' },
                { title: 'Preparatório para Certificações (TOEFL/IELTS)', level: 'Avançado', duration: '6 meses', desc: 'Treinamento intensivo simulado para exames internacionais oficiais.' }
              ].map((c, i) => (
                <div key={i} className="p-8 rounded-3xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col justify-between">
                  <div>
                    <BookOpen className="w-8 h-8 text-indigo-600 mb-4" />
                    <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold uppercase tracking-wider block w-fit mb-3">{c.level}</span>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{c.title}</h3>
                    <p className="text-xs text-slate-500 font-light leading-relaxed mb-6">{c.desc}</p>
                  </div>
                  <button 
                    onClick={() => handleEnrollment(c.title)}
                    className="w-full py-3 rounded-xl bg-slate-100 hover:bg-indigo-600 hover:text-white text-slate-900 font-bold text-xs uppercase tracking-wider transition-colors"
                  >
                    Garantir Vaga
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedTab === 'aluno' && (
          <div className="max-w-4xl mx-auto bg-white p-8 sm:p-12 rounded-3xl border border-slate-200 shadow-md">
            <div className="flex items-center justify-between mb-8 pb-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <UserCheck className="w-6 h-6 text-indigo-600" />
                <div>
                  <h3 className="text-xl font-bold text-slate-900">App Área do Aluno (Demonstração PWA)</h3>
                  <p className="text-xs text-slate-500">Acesso rápido a aulas gravadas, notas e frequência no celular</p>
                </div>
              </div>
              <Badge variant="emerald">Demonstração Ativa</Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>Próxima Aula</span>
                  <span className="text-indigo-600 font-bold">Hoje às 19:00</span>
                </div>
                <h4 className="font-bold text-slate-900 text-base">Conversation Club: Advanced Business</h4>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Duração: 60 minutos com Prof. David</span>
                </div>
              </div>

              <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>Tarefa de Casa</span>
                  <span className="text-amber-600 font-bold">Pendente</span>
                </div>
                <h4 className="font-bold text-slate-900 text-base">Audio Listening Practice #04</h4>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>Prazo final: Amanhã às 23:59</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};
